import { useTranslation } from 'react-i18next'
import { Link, useRouterState } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { useTabBadges } from '@/shared/hooks/useTabBadges'
import { TabBadge } from './TabBadge'
import { TABS } from './BottomNav'

/**
 * Vertical side navigation for tablet (md: 64px rail) and desktop (lg: 200px sidebar).
 * Hidden on mobile — visibility controlled by parent `AppLayout`.
 */
export function SideNav() {
  const { t } = useTranslation('shell')
  const currentPath = useRouterState({ select: (s) => s.location.pathname })
  const badges = useTabBadges()

  return (
    <nav
      aria-label={t('nav.ariaLabel')}
      className="fixed left-0 top-0 h-full z-40 bg-canvas border-r border-line w-16 lg:w-[200px] print:hidden"
    >
      <ul className="flex flex-col items-stretch pt-4 gap-1" role="list">
        {TABS.map(({ path, labelKey, icon: Icon, badgeKey }) => {
          const isActive = currentPath.startsWith(path)
          const badgeCount = badgeKey && !badges.isLoading ? badges[badgeKey] : null
          const ariaLabel = badgeCount
            ? t('nav.badgeLabel', { label: t(labelKey), count: badgeCount })
            : t(labelKey)

          return (
            <li key={path}>
              <Link
                to={path}
                aria-current={isActive ? 'page' : undefined}
                aria-label={ariaLabel}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 lg:px-4',
                  'min-h-[44px] w-full',
                  'text-sm font-medium motion-safe:transition-colors',
                  'justify-center lg:justify-start',
                  isActive ? 'text-accent bg-accent/10' : 'text-muted hover:text-ink hover:bg-surface',
                )}
              >
                <div className="relative flex-shrink-0">
                  <Icon
                    className="h-5 w-5"
                    fill={isActive ? 'currentColor' : 'none'}
                    aria-hidden="true"
                  />
                  {badgeCount && <TabBadge count={badgeCount} />}
                </div>
                <span className="hidden lg:inline truncate">{t(labelKey)}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
